import { Loader2, TableIcon } from 'lucide-react';
import { useState } from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

import { useGetAgeDistribution } from '~entities/studios/api/get-age-distribution';
import { useStudioStore } from '~entities/studios/model/studio-store';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~shared/shadcn/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';

interface AgeDemographicsChartProps {
  sessionId?: string;
}

const AGE_GROUPS = ['0-3', '4-5', '6-8', '9-11', '12-14', '15-18', '18+'];

export function AgeDemographicsChart(_props: AgeDemographicsChartProps) {
  const [showTable, setShowTable] = useState(false);
  const studio = useStudioStore();

  const { data: ageDistribution, isLoading } = useGetAgeDistribution(
    studio.currentStudioId ?? '',
  );

  // Transform data to include all age groups with 0 count for missing ones
  const chartData = AGE_GROUPS.map((group) => {
    const found = ageDistribution?.result.find((d) => d.ageGroup === group);
    return {
      name: group,
      count: found?.count ?? 0,
    };
  });

  if (isLoading) {
    return (
      <Card className='col-span-1'>
        <CardHeader>
          <CardTitle>Age Demographics</CardTitle>
          <CardDescription>
            Distribution of students by age group
          </CardDescription>
        </CardHeader>
        <CardContent className='flex h-[300px] items-center justify-center'>
          <Loader2 className='size-6 animate-spin' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='col-span-1'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0'>
        <div>
          <CardTitle>Age Demographics</CardTitle>
          <CardDescription>Students grouped by age range</CardDescription>
        </div>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            setShowTable(!showTable);
          }}
        >
          <TableIcon className='size-4' />
        </Button>
      </CardHeader>
      <CardContent>
        {showTable ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Age Group</TableHead>
                <TableHead>Number of Students</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((item) => (
                <TableRow key={item.name}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <ChartContainer
            config={{
              value: {
                label: 'Number of Students',
                color: 'hsl(var(--chart-5))',
              },
            }}
            className='h-[250px] w-full overflow-hidden sm:h-[300px]'
          >
            <BarChart data={chartData}>
              <XAxis dataKey='name' />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey='count'
                fill='hsl(var(--chart-5))'
                fillOpacity={1}
                opacity={1}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
      <CardFooter className='text-xs text-muted-foreground'>
        <div className='flex items-center'>
          <span>
            Overall Student Count in the Studio -{' '}
            {ageDistribution?.totalCount ?? 0}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
}
